import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { ImageOff, ShoppingCart, Utensils, X } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  categoryHasAperitivo,
  displayPrice,
  getAperitivoIncrement,
  GROUP_LABELS,
  groupProducts,
  isBurgerExtra,
  isBurgerMain,
  money,
  productId,
  TAPA_EXTRA_PRICE
} from "../lib/cartaHelpers";
import "./CartaConCarrito.css";

function CartaCategoryTabs({ sections, activeCategory, onCategoryClick }) {
  if (!sections.length) return null;
  return (
    <div className="carta-category-tabs" aria-label="Categorias">
      {sections.map(section => {
        const Icon = section.meta.icon;
        const active = section.category === activeCategory;
        return (
          <button
            key={section.category}
            type="button"
            className={`carta-category-tab${active ? " is-active" : ""}`}
            onClick={() => onCategoryClick(section.category)}
            aria-current={active ? "true" : undefined}
          >
            {active && <motion.span className="carta-tab-indicator" layoutId="cc-tab-indicator" />}
            <Icon size={15} aria-hidden="true" />
            <span>{section.meta.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function CartaStickyNav({ activeGroup, activeCategory, counts, sections, onGroupChange, onCategoryClick, navRef, mobileFixed }) {
  return (
    <nav
      ref={navRef}
      className={`carta-sticky-nav${mobileFixed ? " is-mobile-fixed" : ""}`}
      aria-label="Navegacion de carta"
    >
      <div className="carta-nav-inner">
        <Tabs value={activeGroup} onValueChange={onGroupChange} className="carta-mode-tabs">
          <TabsList className="carta-mode-list" aria-label="Tipo de carta">
            {Object.entries(GROUP_LABELS).map(([group,label]) => (
              <TabsTrigger key={group} value={group} className="carta-mode-trigger" disabled={counts[group]===0}>
                {label}<span>{counts[group]}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <CartaCategoryTabs sections={sections} activeCategory={activeCategory} onCategoryClick={onCategoryClick} />
      </div>
    </nav>
  );
}

function CartaProductCard({ product, meta, index, onProductClick, onAdd, onUpdateQty, cartQty, isActive, requiresModalChoice }) {
  const reduce = useReducedMotion();
  const [imgError, setImgError] = useState(false);
  const cardImage = product.thumbnailImageUrl || product.imageUrl || product.detailImageUrl;
  const hasImage = cardImage && !imgError;
  const tags = Array.isArray(product.tags) ? product.tags : [];
  const pid = productId(product);
  const handleQuickAdd = () => {
    if (requiresModalChoice) {
      onProductClick && onProductClick(product);
      return;
    }
    onAdd(product);
  };

  return (
    <motion.article
      className={`carta-product-card carta-product-${meta.group} tc-card`}
      initial={false}
      whileInView={reduce ? undefined : { y:0 }}
      viewport={{ once:true, amount:0.18 }}
      transition={{ duration:0.22, delay:Math.min(index*0.018,0.18) }}
      onClick={() => onProductClick && onProductClick(product)}
      style={{ cursor:"pointer" }}
    >
      <div className="carta-product-media">
        {hasImage ? (
          <img src={cardImage} alt={product.name} loading="lazy" width="88" height="88"
               onError={() => setImgError(true)} />
        ) : (
          <div className="carta-product-fallback"><ImageOff size={22}/><span>{meta.short}</span></div>
        )}
      </div>
      <div className="carta-product-body">
        <div className="carta-product-title-row">
          <h3>{product.name}</h3>
          {product.featured && <span className="carta-featured">Firma</span>}
        </div>
        {product.description && <p className="carta-product-desc">{product.description}</p>}
        {tags.length > 0 && (
          <div className="carta-product-tags">
            {tags.slice(0,3).map(t => <span key={t}>{t}</span>)}
          </div>
        )}
        <div className="tc-mobile-price">{displayPrice(product)}</div>
      </div>
      <div className="tc-card-right" onClick={e => e.stopPropagation()}>
        <div className="carta-product-price">{displayPrice(product)}</div>
        {isActive && (
          requiresModalChoice ? (
            <div className="tc-choice-add">
              {cartQty > 0 && <span className="tc-choice-count">{cartQty}</span>}
              <button className="tc-add-btn" onClick={handleQuickAdd} aria-label={`Configurar ${product.name}`}>
                +
              </button>
            </div>
          ) : cartQty > 0 ? (
            <div className="tc-qty-stepper">
              <button className="tc-qty-btn" onClick={() => onUpdateQty(pid,-1)}>−</button>
              <span className="tc-qty-num">{cartQty}</span>
              <button className="tc-qty-btn" onClick={handleQuickAdd}>+</button>
            </div>
          ) : (
            <button className="tc-add-btn" onClick={handleQuickAdd} aria-label={`Añadir ${product.name}`}>
              +
            </button>
          )
        )}
      </div>
    </motion.article>
  );
}

function CartaSection({ section, onProductClick, onAdd, onUpdateQty, cartQtyMap, isActive, aperitivoExtras = [], burgerExtras = [] }) {
  const Icon = section.meta.icon;
  // Excluir extras de hamburguesa de la carta principal — van al modal
  const mainItems = section.items.filter(product => !isBurgerExtra(product));
  const requiresModalChoice = product => {
    const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
    const hasAperitivo = aperitivoExtras.length > 0 && categoryHasAperitivo(product.category);
    const isBurger = isBurgerMain(product);
    return hasVariants || hasAperitivo || section.meta.group === "bebida" || isBurger;
  };

  return (
    <motion.section data-category={section.category} className="carta-section" initial={false}>
      <div className="carta-section-head">
        <div>
          <p>{mainItems.length} opciones</p>
          <h2><Icon size={22} aria-hidden="true" />{section.meta.label}</h2>
        </div>
        <span className="carta-section-code">{section.meta.short}</span>
      </div>

      <div className="carta-products-grid">
        {mainItems.map((product,index) => (
          <CartaProductCard
            key={productId(product)}
            product={product}
            meta={section.meta}
            index={index}
            onProductClick={onProductClick}
            onAdd={onAdd}
            onUpdateQty={onUpdateQty}
            cartQty={cartQtyMap[productId(product)] || 0}
            isActive={isActive}
            requiresModalChoice={requiresModalChoice(product)}
          />
        ))}
      </div>

      {/* Los extras de hamburguesa se muestran en el modal del producto, no aquí */}
    </motion.section>
  );
}

function CartaProductModal({ product, onClose, onAdd, onUpdateQty, cartQty, cartQtyMap = {}, isActive, aperitivoExtras = [], tapaExtraProducts = [], burgerExtras = [] }) {
  const [imgError, setImgError] = useState(false);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(null);
  const [selectedAperitivo, setSelectedAperitivo] = useState(null);
  const [selectedTapaExtra, setSelectedTapaExtra] = useState(null);
  const [selectedBurgerExtras, setSelectedBurgerExtras] = useState([]); // multi-selección
  const pid = product ? productId(product) : null;

  const toggleBurgerExtra = (extra) => {
    const id = productId(extra);
    setSelectedBurgerExtras(prev =>
      prev.some(e => productId(e) === id)
        ? prev.filter(e => productId(e) !== id)
        : [...prev, extra]
    );
  };

  useEffect(() => {
    if (!product) return;
    setImgError(false);
    setSelectedVariantIndex(null);
    setSelectedAperitivo(null);
    setSelectedTapaExtra(null);
    setSelectedBurgerExtras([]);
    const onKey = event => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [product, onClose]);

  if (!product) return null;

  const modalImage = product.detailImageUrl || product.imageUrl || product.thumbnailImageUrl;
  const imgSrc = modalImage && !imgError ? modalImage : "/assets/neon/no-image-neon.webp";
  const tags = Array.isArray(product.tags) ? product.tags : [];
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const selectedVariant = selectedVariantIndex !== null ? variants[selectedVariantIndex] : null;
  const hasVariants = variants.length > 0;
  const variantRequired = hasVariants && !selectedVariant;
  const basePrice = Number(selectedVariant?.price ?? product.price ?? 0);
  const aperitivoIncrement = selectedAperitivo ? (getAperitivoIncrement(product.category, selectedVariant) ?? 0) : 0;
  const tapaExtraIncrement = selectedTapaExtra ? TAPA_EXTRA_PRICE : 0;
  const burgerExtrasIncrement = selectedBurgerExtras.reduce((sum, e) => sum + Number(e.price || 0), 0);
  const effectivePrice = basePrice + aperitivoIncrement + tapaExtraIncrement + burgerExtrasIncrement;
  const showAperitivoSelector = aperitivoExtras.length > 0 && categoryHasAperitivo(product.category, selectedVariant) && !variantRequired;
  const tapaExtraOptions = product.category === "aperitivos" && Number(product.price) > 0
    ? tapaExtraProducts.filter(ap => productId(ap) !== pid)
    : [];
  const showTapaExtraSelector = tapaExtraOptions.length > 0;
  const showBurgerExtrasSelector = isBurgerMain(product) && burgerExtras.length > 0;
  const variantSuffix = selectedVariant ? `__v_${selectedVariantIndex}` : "";
  const apSuffix = selectedAperitivo ? `__ap_${selectedAperitivo._id || selectedAperitivo.name}` : "";
  const tapaExtraSuffix = selectedTapaExtra ? `__tx_${selectedTapaExtra._id || selectedTapaExtra.name}` : "";
  const burgerExtrasSuffix = selectedBurgerExtras.length > 0
    ? `__bx_${[...selectedBurgerExtras].sort((a,b) => productId(a).localeCompare(productId(b))).map(e => productId(e)).join("_")}`
    : "";
  const selectedCartId = `${pid}${variantSuffix}${apSuffix}${tapaExtraSuffix}${burgerExtrasSuffix}`;
  const qty = cartQtyMap[selectedCartId] ?? cartQty ?? 0;
  const addButtonLabel = variantRequired
    ? "Elige un formato"
    : selectedBurgerExtras.length > 0
      ? `Añadir con ${selectedBurgerExtras.length} extra${selectedBurgerExtras.length > 1 ? "s" : ""}`
      : (selectedAperitivo ? `Añadir con ${selectedAperitivo.name}` : (selectedTapaExtra ? `Añadir con tapa extra` : "Añadir"));

  return (
    <div className="carta-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="carta-modal" onClick={e => e.stopPropagation()}>
        <button className="carta-modal-close" onClick={onClose} aria-label="Cerrar">✕</button>
        <div className="carta-modal-media">
          <img src={imgSrc} alt={product.name} loading="lazy" onError={() => setImgError(true)} />
        </div>
        <div className="carta-modal-body">
          <div className="carta-modal-header">
            <div className="carta-modal-title-wrap">
              <h2 className="carta-modal-name">{product.name}</h2>
              {product.featured && <span className="carta-featured">Firma</span>}
            </div>
            <div className="carta-modal-price">
              {money(effectivePrice)}
              {selectedVariant && (
                <span className="tc-variant-price-detail"> ({selectedVariant.label})</span>
              )}
              {selectedAperitivo && aperitivoIncrement > 0 && (
                <span className="tc-aperitivo-price-detail">
                  {" "}+ {aperitivoIncrement.toFixed(2)} € aperitivo
                </span>
              )}
              {selectedTapaExtra && (
                <span className="tc-aperitivo-price-detail">
                  {" "}+ {TAPA_EXTRA_PRICE.toFixed(2)} € tapa extra
                </span>
              )}
            </div>
          </div>
          {product.description && <p className="carta-modal-desc">{product.description}</p>}
          {tags.length > 0 && (
            <div className="carta-modal-tags">
              <p className="carta-modal-tags-label">Ingredientes</p>
              <div className="carta-product-tags">{tags.map(t => <span key={t}>{t}</span>)}</div>
            </div>
          )}
          {hasVariants && (
            <div className="tc-variant-selector">
              <p className="tc-variant-label">
                Elige formato
                {variantRequired && <span className="tc-variant-required">*</span>}
              </p>
              <div className="tc-variant-chips">
                {variants.map((variant, index) => {
                  const isSelected = selectedVariantIndex === index;
                  return (
                    <button
                      key={`${variant.label}-${index}`}
                      type="button"
                      className={`tc-variant-chip${isSelected ? " is-selected" : ""}`}
                      onClick={() => {
                        setSelectedVariantIndex(isSelected ? null : index);
                        setSelectedAperitivo(null);
                        setSelectedTapaExtra(null);
                      }}
                      aria-pressed={isSelected}
                    >
                      <span className="tc-variant-chip-label">{variant.label}</span>
                      <span className="tc-variant-chip-price">{Number(variant.price).toFixed(2)} €</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {showAperitivoSelector && (
            <div className="tc-aperitivo-selector" aria-label="Asistente de aperitivo">
              <div className="tc-aperitivo-head">
                <p className="tc-aperitivo-label">
                  <Utensils size={14} aria-hidden="true" />
                  ¿Quieres aperitivo?
                </p>
                <span className="tc-aperitivo-summary">
                  {selectedAperitivo ? `Con ${selectedAperitivo.name}` : "Sin aperitivo"}
                </span>
              </div>
              <div className="tc-aperitivo-choice-row">
                <button
                  type="button"
                  className={`tc-aperitivo-choice${!selectedAperitivo ? " is-selected" : ""}`}
                  onClick={() => setSelectedAperitivo(null)}
                  aria-pressed={!selectedAperitivo}
                >
                  <span className="tc-aperitivo-choice-main">Sin aperitivo</span>
                  <span className="tc-aperitivo-choice-sub">Solo {product.name}</span>
                </button>
              </div>
              <p className="tc-aperitivo-subtitle">Con aperitivo</p>
              <div className="tc-aperitivo-chips">
                {aperitivoExtras.map(ap => {
                  const inc = getAperitivoIncrement(product.category, selectedVariant) ?? 0;
                  const isSelected = selectedAperitivo ? productId(selectedAperitivo) === productId(ap) : false;
                  return (
                    <button
                      key={ap._id || ap.name}
                      type="button"
                      className={`tc-aperitivo-chip${isSelected ? " is-selected" : ""}`}
                      onClick={() => setSelectedAperitivo(isSelected ? null : ap)}
                      aria-pressed={isSelected}
                    >
                      <span className="tc-aperitivo-chip-name">{ap.name}</span>
                      <span className="tc-aperitivo-chip-price">+{inc.toFixed(2)} €</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {showBurgerExtrasSelector && (
            <div className="tc-aperitivo-selector tc-burger-extras-selector" aria-label="Extras de hamburguesa">
              <div className="tc-aperitivo-head">
                <p className="tc-aperitivo-label">
                  <Utensils size={14} aria-hidden="true" />
                  Personaliza tu hamburguesa
                </p>
                <span className="tc-aperitivo-summary">
                  {selectedBurgerExtras.length > 0
                    ? selectedBurgerExtras.map(e => e.name.replace("Extra: ", "")).join(", ")
                    : "Sin extras"}
                </span>
              </div>
              <div className="tc-burger-extras-grid">
                {burgerExtras.map(extra => {
                  const isSelected = selectedBurgerExtras.some(e => productId(e) === productId(extra));
                  return (
                    <button
                      key={productId(extra)}
                      type="button"
                      className={`tc-burger-extra-chip${isSelected ? " is-selected" : ""}`}
                      onClick={() => toggleBurgerExtra(extra)}
                      aria-pressed={isSelected}
                    >
                      <span className="tc-burger-extra-name">{extra.name.replace("Extra: ", "")}</span>
                      <span className="tc-burger-extra-price">+{Number(extra.price).toFixed(2)} €</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {showTapaExtraSelector && (
            <div className="tc-aperitivo-selector" aria-label="Tapa extra">
              <div className="tc-aperitivo-head">
                <p className="tc-aperitivo-label">
                  <Utensils size={14} aria-hidden="true" />
                  ¿Añadir tapa extra?
                </p>
                <span className="tc-aperitivo-summary">
                  {selectedTapaExtra ? `Extra: ${selectedTapaExtra.name}` : "Sin tapa extra"}
                </span>
              </div>
              <div className="tc-aperitivo-choice-row">
                <button
                  type="button"
                  className={`tc-aperitivo-choice${!selectedTapaExtra ? " is-selected" : ""}`}
                  onClick={() => setSelectedTapaExtra(null)}
                  aria-pressed={!selectedTapaExtra}
                >
                  <span className="tc-aperitivo-choice-main">Sin tapa extra</span>
                  <span className="tc-aperitivo-choice-sub">Solo {product.name}</span>
                </button>
              </div>
              <p className="tc-aperitivo-subtitle">Tapa extra</p>
              <div className="tc-aperitivo-chips">
                {tapaExtraOptions.map(ap => {
                  const isSelected = selectedTapaExtra ? productId(selectedTapaExtra) === productId(ap) : false;
                  return (
                    <button
                      key={ap._id || ap.name}
                      type="button"
                      className={`tc-aperitivo-chip${isSelected ? " is-selected" : ""}`}
                      onClick={() => setSelectedTapaExtra(isSelected ? null : ap)}
                      aria-pressed={isSelected}
                    >
                      <span className="tc-aperitivo-chip-name">{ap.name}</span>
                      <span className="tc-aperitivo-chip-price">+{TAPA_EXTRA_PRICE.toFixed(2)} €</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {isActive && (
            <div className="tc-modal-actions" onClick={e => e.stopPropagation()}>
              {showAperitivoSelector || showTapaExtraSelector || showBurgerExtrasSelector || hasVariants ? (
                <>
                  {qty > 0 && (
                    <p className="tc-modal-selected-qty">
                      Ya en carrito con esta opción: {qty}
                    </p>
                  )}
                  <button
                    className="tc-modal-add-btn"
                    disabled={variantRequired}
                    title={variantRequired ? "Elige un formato primero" : undefined}
                    onClick={() => {
                      if (variantRequired) return;
                      onAdd(product, selectedVariant, selectedAperitivo, selectedTapaExtra, selectedBurgerExtras);
                      onClose();
                    }}
                  >
                    {addButtonLabel} — {money(effectivePrice)}
                  </button>
                </>
              ) : qty > 0 ? (
                <div className="tc-modal-stepper">
                  <button className="tc-qty-btn tc-qty-btn-lg" onClick={() => onUpdateQty(selectedCartId,-1)}>−</button>
                  <span className="tc-qty-num-lg">{qty}</span>
                  <button className="tc-qty-btn tc-qty-btn-lg" onClick={() => onAdd(product)}>+</button>
                </div>
              ) : (
                <button className="tc-modal-add-btn" onClick={() => { onAdd(product); onClose(); }}>
                  {addButtonLabel} — {money(effectivePrice)}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CartaCartPanel({ open, onClose, cart, total, isActive, onUpdateQty, onUpdateNotes, onSend, sending, sent, orderError, title }) {
  if (!open) return null;
  return (
    <div className="tc-cart-overlay" onClick={onClose}>
      <div className="tc-cart-panel" onClick={e => e.stopPropagation()}>
        <div className="tc-cart-head">
          <h2 className="tc-cart-title">{title || "Tu pedido"}</h2>
          <button className="tc-cart-close" onClick={onClose} aria-label="Cerrar"><X size={18}/></button>
        </div>

        {sent && (
          <div className="tc-order-sent">
            <span>✓</span> Pedido enviado correctamente.
          </div>
        )}
        {orderError && <div className="tc-order-error">{orderError}</div>}

        {cart.length === 0 ? (
          <p className="tc-cart-empty">El carrito está vacío.</p>
        ) : (
          <>
            <ul className="tc-cart-list">
              {cart.map(item => (
                <li key={item.id} className="tc-cart-item">
                  <div className="tc-cart-item-info">
                    <span className="tc-cart-item-name">{item.name}</span>
                    <span className="tc-cart-item-price">{(item.price * item.qty).toFixed(2)} €</span>
                  </div>
                  <div className="tc-cart-item-controls">
                    <input
                      className="tc-cart-notes"
                      placeholder="Nota..."
                      value={item.notes || ""}
                      onChange={e => onUpdateNotes(item.id, e.target.value)}
                      onClick={e => e.stopPropagation()}
                    />
                    <div className="tc-qty-stepper">
                      <button className="tc-qty-btn" onClick={() => onUpdateQty(item.id,-1)}>−</button>
                      <span className="tc-qty-num">{item.qty}</span>
                      <button className="tc-qty-btn" onClick={() => onUpdateQty(item.id,1)}>+</button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="tc-cart-footer">
              <div className="tc-cart-total-row">
                <span>Total</span>
                <span className="tc-cart-total-val">{total.toFixed(2)} €</span>
              </div>
              <button
                className="tc-send-btn"
                disabled={!isActive || sending || cart.length === 0}
                onClick={onSend}
              >
                {sending ? "Enviando..." : "Enviar pedido"}
              </button>
              {!isActive && <p className="tc-inactive-msg">La mesa está inactiva.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function CartaConCarrito({
  products = [],
  aperitivoExtras,
  isActive = true,
  onSendOrder,
  title,
  onCartChange,
  cartOpenSignal = 0,
}) {
  const [activeGroup, setActiveGroup] = useState("bebida");
  const [activeCategory, setActiveCategory] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [orderError, setOrderError] = useState("");
  const [mobileNavFixed, setMobileNavFixed] = useState(false);
  const sentinelRef = useRef(null);
  const navRef = useRef(null);
  const mainRef = useRef(null);

  const effectiveAperitivoExtras = useMemo(
    () => Array.isArray(aperitivoExtras)
      ? aperitivoExtras
      : products.filter(product => {
          const price = Number(product.price);
          return product.category === "aperitivos" &&
            product.active !== false &&
            price > 0 &&
            product.name !== "Tapa Extra";
        }),
    [aperitivoExtras, products]
  );

  const effectiveTapaExtraProducts = useMemo(
    () => products.filter(product => product.category === "aperitivos" && product.active !== false && Number(product.price) > 0),
    [products]
  );

  // Extras de hamburguesa — productos de categoría hamburguesas con nombre "Extra: ..."
  const effectiveBurgerExtras = useMemo(
    () => products.filter(product => isBurgerExtra(product) && product.active !== false),
    [products]
  );

  const sections = useMemo(() => groupProducts(products), [products]);
  const counts = useMemo(() => sections.reduce((acc, section) => {
    acc[section.meta.group] = (acc[section.meta.group] || 0) + section.items.length;
    return acc;
  }, { bebida:0, comida:0 }), [sections]);
  const visibleSections = useMemo(
    () => sections.filter(section => section.meta.group === activeGroup),
    [sections, activeGroup]
  );

  useEffect(() => {
    if (!visibleSections.length) return;
    if (!visibleSections.some(section => section.category === activeCategory)) {
      setActiveCategory(visibleSections[0].category);
    }
  }, [activeCategory, visibleSections]);

  const currentSectionIdx = useMemo(
    () => visibleSections.findIndex(section => section.category === activeCategory),
    [visibleSections, activeCategory]
  );
  const currentSection = visibleSections[currentSectionIdx] ?? null;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (window.innerWidth < 768)
        setMobileNavFixed(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    }, { threshold:0 });
    observer.observe(sentinel);
    const onResize = () => { if (window.innerWidth >= 768) setMobileNavFixed(false); };
    window.addEventListener("resize", onResize);
    return () => { observer.disconnect(); window.removeEventListener("resize", onResize); };
  }, []);

  const cartQtyMap = useMemo(() => {
    const map = {};
    for (const item of cart) map[item.id] = item.qty;
    return map;
  }, [cart]);

  const cartProductQtyMap = useMemo(() => {
    const map = {};
    for (const item of cart) {
      const baseId = item.productId || String(item.id).split("__ap_")[0];
      map[baseId] = (map[baseId] || 0) + item.qty;
    }
    return map;
  }, [cart]);

  const cartTotal = cart.reduce((sum,item) => sum + item.price*item.qty, 0);
  const cartCount = cart.reduce((sum,item) => sum + item.qty, 0);

  useEffect(() => {
    onCartChange?.({ cart, cartCount, cartTotal });
  }, [cart, cartCount, cartTotal, onCartChange]);

  useEffect(() => {
    if (cartOpenSignal > 0 && cart.length > 0) setCartOpen(true);
  }, [cartOpenSignal, cart.length]);

  const scrollToSection = useCallback(() => {
    if (!mainRef.current) return;
    const top = mainRef.current.getBoundingClientRect().top + window.scrollY - 66;
    window.scrollTo({ top:Math.max(0,top), behavior:"smooth" });
  }, []);

  const scrollToCategory = useCallback((category) => {
    setActiveCategory(category);
    setTimeout(scrollToSection, 60);
  }, [scrollToSection]);

  const selectGroup = useCallback((group) => {
    setActiveGroup(group);
    const first = sections.find(section => section.meta.group === group);
    if (first) scrollToCategory(first.category);
  }, [sections, scrollToCategory]);

  const goNext = useCallback(() => {
    if (currentSectionIdx < visibleSections.length - 1)
      scrollToCategory(visibleSections[currentSectionIdx + 1].category);
  }, [currentSectionIdx, visibleSections, scrollToCategory]);

  const goPrev = useCallback(() => {
    if (currentSectionIdx > 0)
      scrollToCategory(visibleSections[currentSectionIdx - 1].category);
  }, [currentSectionIdx, visibleSections, scrollToCategory]);

  const addToCart = useCallback((product, variant = null, aperitivo = null, tapaExtra = null, burgerExtrasList = []) => {
    if (!isActive) return;
    const pid = productId(product);
    const variantIndex = variant ? (Array.isArray(product.variants) ? product.variants.findIndex(item => item.label === variant.label) : -1) : null;
    const variantSuffix = variant ? `__v_${variantIndex}` : "";
    const apSuffix = aperitivo ? `__ap_${aperitivo._id || aperitivo.name}` : "";
    const tapaExtraSuffix = tapaExtra ? `__tx_${tapaExtra._id || tapaExtra.name}` : "";
    const burgerExtrasSuffix = burgerExtrasList.length > 0
      ? `__bx_${[...burgerExtrasList].sort((a,b) => productId(a).localeCompare(productId(b))).map(e => productId(e)).join("_")}`
      : "";
    const burgerExtrasPrice = burgerExtrasList.reduce((sum, e) => sum + Number(e.price || 0), 0);
    const effectivePrice = Number(variant?.price ?? product.price ?? 0) +
      (aperitivo ? (getAperitivoIncrement(product.category, variant) ?? 0) : 0) +
      (tapaExtra ? TAPA_EXTRA_PRICE : 0) +
      burgerExtrasPrice;
    const cartId = `${pid}${variantSuffix}${apSuffix}${tapaExtraSuffix}${burgerExtrasSuffix}`;
    const displayName = [
      product.name,
      variant?.label,
      aperitivo ? `+ ${aperitivo.name}` : "",
      tapaExtra ? `+ tapa extra: ${tapaExtra.name}` : "",
      ...burgerExtrasList.map(e => `+ ${e.name.replace("Extra: ", "")}`)
    ].filter(Boolean).join(" ");
    setCart(prev => {
      const existing = prev.find(item => item.id === cartId);
      return existing
        ? prev.map(item => item.id === cartId ? { ...item, qty:item.qty+1 } : item)
        : [...prev, {
            id:cartId,
            productId:pid,
            variantIndex:variantIndex !== null && variantIndex >= 0 ? variantIndex : null,
            variantLabel:variant?.label || "",
            aperitivoId:aperitivo ? productId(aperitivo) : null,
            aperitivoName:aperitivo?.name || "",
            tapaExtraId:tapaExtra ? productId(tapaExtra) : null,
            tapaExtraName:tapaExtra?.name || "",
            burgerExtras:burgerExtrasList.map(e => ({ id: productId(e), name: e.name, price: e.price })),
            name:displayName,
            price:effectivePrice,
            qty:1,
            notes:""
          }];
    });
  }, [isActive]);

  const updateQty = useCallback((id, delta) => {
    setCart(prev => prev.map(item => item.id===id ? {...item,qty:item.qty+delta} : item).filter(item => item.qty>0));
  }, []);

  const updateNotes = useCallback((id, notes) => {
    setCart(prev => prev.map(item => item.id===id ? {...item,notes} : item));
  }, []);

  const handleSend = useCallback(async () => {
    if (!cart.length || !isActive || !onSendOrder) return;
    setSending(true);
    setOrderError("");
    try {
      await onSendOrder(cart);
      setCart([]);
      setSent(true);
      setCartOpen(false);
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      setOrderError(err.message || "Error al enviar el pedido");
      setCartOpen(true);
    } finally {
      setSending(false);
    }
  }, [cart, isActive, onSendOrder]);

  return (
    <div className="carta-con-carrito">
      <div ref={sentinelRef} style={{ height:0, pointerEvents:"none" }} aria-hidden="true" />
      <CartaStickyNav
        activeGroup={activeGroup}
        activeCategory={activeCategory}
        counts={counts}
        sections={visibleSections}
        onGroupChange={selectGroup}
        onCategoryClick={scrollToCategory}
        navRef={navRef}
        mobileFixed={mobileNavFixed}
      />
      {mobileNavFixed && <div className="carta-nav-ph" aria-hidden="true" />}

      <main className="carta-main" ref={mainRef}>
        {sent && (
          <div className="tc-order-sent-banner">
            ✓ Pedido enviado
          </div>
        )}

        {!currentSection ? (
          <div className="carta-status">
            <span /><h2>Sin productos</h2><p>No hay productos disponibles en esta categoría.</p>
          </div>
        ) : (
          <>
            <CartaSection
              key={currentSection.category}
              section={currentSection}
              onProductClick={setSelectedProduct}
              onAdd={addToCart}
              onUpdateQty={updateQty}
              cartQtyMap={cartProductQtyMap}
              isActive={isActive}
              aperitivoExtras={effectiveAperitivoExtras}
              burgerExtras={effectiveBurgerExtras}
            />
            <div className="carta-section-nav">
              {currentSectionIdx > 0 ? (
                <button className="carta-nav-btn carta-nav-prev" onClick={goPrev}>
                  ↑ {visibleSections[currentSectionIdx-1].meta.label}
                </button>
              ) : <span />}
              <span className="carta-nav-pos">
                {currentSectionIdx+1} / {visibleSections.length}
              </span>
              {currentSectionIdx < visibleSections.length-1 ? (
                <button className="carta-nav-btn carta-nav-next" onClick={goNext}>
                  {visibleSections[currentSectionIdx+1].meta.label} ↓
                </button>
              ) : <span />}
            </div>
          </>
        )}
      </main>

      <CartaProductModal
        product={selectedProduct}
        onClose={() => setSelectedProduct(null)}
        onAdd={addToCart}
        onUpdateQty={updateQty}
        cartQty={selectedProduct ? cartQtyMap[productId(selectedProduct)] : 0}
        cartQtyMap={cartQtyMap}
        isActive={isActive}
        aperitivoExtras={effectiveAperitivoExtras}
        tapaExtraProducts={effectiveTapaExtraProducts}
        burgerExtras={effectiveBurgerExtras}
      />

      <CartaCartPanel
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        total={cartTotal}
        isActive={isActive}
        onUpdateQty={updateQty}
        onUpdateNotes={updateNotes}
        onSend={handleSend}
        sending={sending}
        sent={sent}
        orderError={orderError}
        title={title}
      />

      {isActive && cart.length > 0 && (
        <button className="tc-cart-fab" onClick={() => setCartOpen(true)} aria-label={`Abrir carrito — ${cartCount} productos`}>
          <ShoppingCart size={20} />
          <span className="tc-cart-badge">{cartCount}</span>
          <span className="tc-cart-total">{cartTotal.toFixed(2)} €</span>
        </button>
      )}
    </div>
  );
}
